import {
  FieldHelperText,
  LabelWithIcon,
  getIconForProperty,
  type FieldProps,
  type StringProperty,
} from "@firecms/core";
import {
  cls,
  defaultBorderMixin,
  fieldBackgroundMixin,
} from "@firecms/ui";
import {
  OTPInput,
  REGEXP_ONLY_DIGITS_AND_CHARS,
  type SlotProps,
} from "input-otp";

import { formatFieldError } from "../localization/formatFieldError";
import { ACCESS_CODE_LENGTH, normalizeAccessCode } from "./accessCode";

function AccessCodeSlot({
  isActive,
  char,
  hasFakeCaret,
  disabled,
}: SlotProps & { disabled?: boolean }) {
  return (
    <div
      className={cls(
        "relative flex h-12 w-10 items-center justify-center rounded-md border text-lg font-semibold uppercase transition-colors",
        fieldBackgroundMixin,
        defaultBorderMixin,
        "text-text-primary dark:text-text-primary-dark",
        isActive && "border-primary ring-2 ring-primary/30 dark:border-primary",
        disabled && "opacity-50",
      )}
    >
      {char}
      {hasFakeCaret ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-6 w-px animate-pulse bg-primary" />
        </div>
      ) : null}
    </div>
  );
}

export function AccessCodeOtpField({
  property,
  value,
  setValue,
  disabled,
  showError,
  error,
  includeDescription,
}: FieldProps<string, StringProperty>) {
  const displayError = formatFieldError(error);

  return (
    <div className="flex w-full flex-col gap-2">
      <LabelWithIcon
        icon={getIconForProperty(property, "small")}
        required={property.validation?.required}
        title={property.name}
      />

      <OTPInput
        value={value ?? ""}
        onChange={(next) => setValue(normalizeAccessCode(next))}
        maxLength={ACCESS_CODE_LENGTH}
        pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
        pasteTransformer={normalizeAccessCode}
        disabled={disabled}
        containerClassName="flex gap-2 has-disabled:opacity-60"
        inputMode="text"
        autoCapitalize="characters"
        autoComplete="one-time-code"
        render={({ slots }) => (
          <>
            {slots.map((slot, index) => (
              <AccessCodeSlot
                key={index}
                {...slot}
                disabled={disabled}
              />
            ))}
          </>
        )}
      />

      <FieldHelperText
        includeDescription={includeDescription}
        showError={showError && Boolean(displayError)}
        error={displayError}
        disabled={disabled}
        property={property}
      />
    </div>
  );
}
